import { Form, Input } from "antd";
import { useUpdateCommissionGroupMutation } from "api/TenantSlug/CommissionGroup/hooks";
import { userGroup } from "components/Tables/CommissionGroupTable/temp";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { t } from "i18next";
import { FC } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { z } from "zod";

import { CommissionGroupEditFormData, formSchema } from "./formSchema";

import CommissionGroupData = App.Data.CommissionGroupData;
import StoreCommissionGroupRequest = App.Http.Requests.CommissionGroup.StoreCommissionGroupRequest;


interface ContentProps {
    commissionGroup: CommissionGroupData;
    onSubmit?: () => void;
    setLocalCommissions?: (callback: (prev: CommissionGroupData[]) => CommissionGroupData[]) => void;
}

const CommissionGroupEditFormContent: FC<ContentProps> = () => {
    const { control, fieldNames } = useGeneralForm<CommissionGroupEditFormData>();
    const { t } = usePaldockTranslation("forms.commission-group-edit");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");

    return (
        <DrawerContent>
            <Form layout="vertical">
                <DrawerCollapsible title={t("sections.commission_group")}>
                    <FormItem control={control} name={fieldNames.title.name} label={tFields("title.label")}>
                        <Input />
                    </FormItem>
                    <FormItem control={control} name={fieldNames.description?.name ?? "description"} label={tFields("description.label")}>
                        <Input />
                    </FormItem>
                </DrawerCollapsible>
            </Form>
        </DrawerContent>
    );
};

interface CommissionGroupEditFormProps {
    formId?: string;
    commissionGroup: CommissionGroupData;
    onSubmit?: () => void;
    setLocalCommissions?: (callback: (prev: CommissionGroupData[]) => CommissionGroupData[]) => void;
}

export const CommissionGroupEditForm: FC<CommissionGroupEditFormProps> = ({
    formId,
    commissionGroup,
    onSubmit,
    setLocalCommissions,
}) => {
    const { handleErrorsFromErrorResponse } = useGeneralForm<CommissionGroupEditFormData>();
    const defaultValues = {
        title: commissionGroup.title,
        description: commissionGroup.description ?? undefined
    };
    const updateCommissionGroupMutation = useUpdateCommissionGroupMutation(commissionGroup.id);

    const onSubmitForm = (values: CommissionGroupEditFormData) => {
        try {
            formSchema.parse(values);

            if (commissionGroup.id === userGroup.id) {
                setLocalCommissions?.((prev) =>
                    prev.map((item) => (item.id === userGroup.id ? { ...item, ...values } : item))
                );
                toast.success(t("messages.update_success", { title: values.title }));
            } else {
                updateCommissionGroupMutation.mutate(values as StoreCommissionGroupRequest, {
                    onSuccess: () => {
                        onSubmit?.();
                        toast.success(t("messages.update_success"));
                    },
                    onError: (error) => {
                        toast.error(t("messages.update_error"));
                        handleErrorsFromErrorResponse(error);
                    },
                });
            }
        } catch (error) {
            if (error instanceof z.ZodError) {
                error.errors.forEach((err) => {
                    toast.error(err.message);
                });
            } else {
                toast.error(t("messages.update_error"));
            }
        }
    };

    return (
        <GeneralFormProvider<CommissionGroupEditFormData>
            id={formId}
            schema={formSchema}
            defaultValues={defaultValues}
            onSubmit={onSubmitForm}
        >
            <CommissionGroupEditFormContent commissionGroup={commissionGroup} onSubmit={onSubmit} />
        </GeneralFormProvider>
    );
};
